import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TradingChart } from "./TradingChart";
import { Card } from "./ui/card";
import { BinancePairsModal } from "./BinancePairsModal";
import { socket } from "../config/socket.io";
import Axios from "../Axios";
import { ResultModal } from "./ResultModal";
import { useWallet } from "../contexts/walletContext.js";
import { playSound } from "./Soundmaker";
import { Sparkles, TrendingDown } from "lucide-react";
import {
  ROUND_VARIANTS,
  SETTLEMENT_PHASE_MS,
  PAYOUT_PHASE_MS,
  OUTCOME_SLOT_COUNT,
  RACE_TIMER_LEAD_START_PERCENT,
  RACE_TIMER_LEAD_END_PERCENT,
  RACE_FLAG_GAP_PERCENT,
  LIVE_MARKER_FALLBACK_PERCENT,
  FIXED_PAYOUT_PERCENT,
  FIXED_PAYOUT_MULTIPLIER,
  DEFAULT_ORDER_DURATION_SECONDS,
  CUSTOM_EXPIRY_ID,
  CHART_INTERVALS,
  EXPIRY_OPTIONS,
  AMOUNT_PICKER_OPTIONS,
  orderStickyEndTimes,
} from "./updown/constants/upDownConstants";
import { forgetResultOwner } from "./updown/utils/resultOwnerManager";
import {
  formatPairLabelFromSymbol,
  toBackendPairSymbol,
  parseStake,
  clamp,
  clampCustomExpirySeconds,
  roundToCents,
  clampStake,
  toAmountInput,
  formatUsd,
} from "./updown/utils/upDownFormatters";
import { EarlyCloseModal } from "./updown/components/EarlyCloseModal";
import { TimePickerOverlay } from "./updown/components/TimePickerOverlay";
import { AmountPickerDropdown } from "./updown/components/AmountPickerDropdown";
import { usePickerPositioning } from "./updown/hooks/usePickerPositioning";
import { useUpDownSocket } from "./updown/hooks/useUpDownSocket";
import { useUpDownOrders } from "./updown/hooks/useUpDownOrders";
import { useUpDownParticipants } from "./updown/hooks/useUpDownParticipants";
import { UpDownToolbar } from "./updown/components/UpDownToolbar";
import { UpDownActiveTradesList } from "./updown/components/UpDownActiveTradesList";
import { UpDownExecutionDock } from "./updown/components/UpDownExecutionDock";
import { InFlightDuelOverlay } from "./effects/InFlightDuelOverlay";
import { RpgCriticalWinBanner } from "./effects/RpgCriticalWinBanner";
import "./effects/gamification-ui.css";

export function UpDownTradingPanel({
  panelId = "single",
  symbol,
  precision,
  tickSize,
  luckyData,
  getLuckyRoundDetails,
  marketSymbol,
  startingPrice,
  timeframe = "Tick",
  onTimeframeChange = () => {},
  chartType = "candle",
  pairType = "crypto",
  setPairType,
  onChartTypeChange = () => {},
  pairs = [],
  selectedPair,
  onSelectPair = () => {},
  activePairStats,
  hasMorePairs = false,
  isLoadingMorePairs = false,
  onLoadMorePairs,
  chartFontSize = null,
  layoutVariant = false,
  activeLayout,
  onLayoutChange,
  layoutRef,
  showLayoutMulti = false,
  shouldShowLuckyModal = true,
  onShowSpin,
  tradingAPIRef,
}) {
  const chartRef = useRef(null);
  // ÃƒÂ°Ã…Â¸Ã¢â‚¬ÂÃ‚Â§ FIX: Removed localStorage retrieval (readStoredTradeSession), initialized to defaults
  const restoredUiState = {};
  const restoredExpiryId = ROUND_VARIANTS[0].id;
  const restoredVariantId = ROUND_VARIANTS[0].id;
  const restoredCustomExpirySeconds = DEFAULT_ORDER_DURATION_SECONDS;
  const restoredOrder = null;
  const restoredActiveOrders = [];
  // Round Stats
  const [roundDetails, setRoundDetails] = useState(() => ({
    roundId: "",
    openPrice: 0,
    status: "",
    pair: "",
    ...(restoredUiState.roundDetails &&
    typeof restoredUiState.roundDetails === "object"
      ? restoredUiState.roundDetails
      : {}),
  }));

  const [amountInput, setAmountInput] = useState("");

  const isPairBootstrappedRef = useRef(false);
  const variantRef = useRef(ROUND_VARIANTS[0]);
  const roundIndexRef = useRef(1);
  const startingPriceRef = useRef(startingPrice);
  const betsRef = useRef([]);
  const liveMarkerXPercentRef = useRef(LIVE_MARKER_FALLBACK_PERCENT);
  const livePricePollAtRef = useRef(0);
  const clockPaintAtRef = useRef(0);
  const hasAlignedInitialRoundRef = useRef(false);
  const joinKickoffTimerRef = useRef(null);
  const joinNextTimerRef = useRef(null);
  const pairRoundResetTimerRef = useRef(null);
  const joinBatchRef = useRef([]);
  const activeRoundStartRef = useRef(0);
  const settledToastKeysRef = useRef(new Set());
  const pairTriggerRef = useRef(null);
  const socketRef = useRef(null);
  const currentRoundIdRef = useRef("");
  const tradeParticipantsRevealTimerRef = useRef(null);
  const tradePanelCloseTimerRef = useRef(null);
  const tradePanelFinalizeTimerRef = useRef(null);
  const localResultOrderIdsRef = useRef(new Set());
  const chartOrderHydrationRef = useRef(new Set());
  const orderRemovalTimersRef = useRef(new Map());
  const activeOrdersDataRef = useRef(null); // Prevent unnecessary state updates
  const settledOrderIdsRef = useRef(new Set()); // Track trades settled via socket to prevent re-adding from stale API responses
  const justSettledTradeRef = useRef(false); // Track if a trade just settled to conditionally show the popup
  const ignoredBackendTradeIdsRef = useRef(new Set()); // Track backend trades that correspond to our optimistic trades
  const backendIdToLocalIdRef = useRef(new Map()); // Map backend _id to local order-xxx ID
  const resultShownIdsRef = useRef(new Set()); // Track orders that already showed result modal
  const isFirstSettingsLoadRef = useRef(true);
  const [currentRoundBids, setCurrentRoundBids] = useState(() =>
    Array.isArray(restoredUiState.currentRoundBids)
      ? restoredUiState.currentRoundBids
      : [],
  );
  const [variantId, setVariantId] = useState(restoredVariantId);
  const [phase, setPhase] = useState(restoredUiState.phase ?? "trading");
  const [phaseStartedAt, setPhaseStartedAt] = useState(
    () => Number(restoredUiState.phaseStartedAt) || Date.now(),
  );
  const [phaseEndsAt, setPhaseEndsAt] = useState(
    () =>
      Number(restoredUiState.phaseEndsAt) ||
      Date.now() + ROUND_VARIANTS[0].tradeWindowSeconds * 1000,
  );
  const [clockNow, setClockNow] = useState(() => Date.now());
  const [roundIndex, setRoundIndex] = useState(
    () => Number(restoredUiState.roundIndex) || 1,
  );
  const [roundStartPrice, setRoundStartPrice] = useState(
    () => Number(restoredUiState.roundStartPrice) || startingPrice,
  );
  const [livePrice, setLivePrice] = useState(
    () => Number(restoredUiState.livePrice) || startingPrice,
  );
  const [tickerOffset, setTickerOffset] = useState(0);
  const [bets, setBets] = useState(() =>
    Array.isArray(restoredUiState.bets) ? restoredUiState.bets : [],
  );
  const [settlement, setSettlement] = useState(
    restoredUiState.settlement ?? null,
  );
  const [recentOutcomes, setRecentOutcomes] = useState(() =>
    Array.isArray(restoredUiState.recentOutcomes)
      ? restoredUiState.recentOutcomes
      : [],
  );
  const [isRoundJoining, setIsRoundJoining] = useState(false);
  const [isPairListOpen, setIsPairListOpen] = useState(false);
  // const [wallet, setWallet] = useState({ balance: 0 });
  const { wallet, setWallet } = useWallet();
  // ÃƒÂ°Ã…Â¸Ã¢â‚¬ÂÃ‚Â§ FIX: Added loading state and removed unused activeTrades
  const [isLoadingActiveOrders, setIsLoadingActiveOrders] = useState(false);
  const [selectedExpiryId, setSelectedExpiryId] = useState(null);
  const [customExpirySeconds, setCustomExpirySeconds] = useState(() =>
    String(restoredCustomExpirySeconds),
  );
  const [timerInput, setTimerInput] = useState("00:10");
  const {
    isTimePickerOpen,
    setIsTimePickerOpen,
    timePickerPosition,
    timePickerTriggerRef,
    timePickerPanelRef,
    timePickerScrollRef,
    selectedTimeOptionRef,
    openTimePicker,
    closeTimePicker,
    isAmountPickerOpen,
    setIsAmountPickerOpen,
    amountPickerPosition,
    amountPickerTriggerRef,
    amountPickerPanelRef,
    openAmountPicker,
    closeAmountPicker,
  } = usePickerPositioning({ timerInput });

  const handleTimeOptionSelect = useCallback(
    (option) => {
      setSelectedExpiryId(option?.id);
      setCustomExpirySeconds(String(option?.seconds ?? option));
      setTimerInput(option?.value ?? String(option));
      closeTimePicker();
    },
    [closeTimePicker],
  );

  const handleAmountOptionSelect = useCallback(
    (value) => {
      setAmountInput(String(value));
      closeAmountPicker();
    },
    [closeAmountPicker],
  );

  useEffect(() => {
    const handleSetTime = (e) => {
      const { seconds } = e.detail;
      const m = Math.floor(seconds / 60).toString().padStart(2, '0');
      const s = (seconds % 60).toString().padStart(2, '0');
      setTimerInput(`${m}:${s}`);
      setSelectedExpiryId("custom");
      setCustomExpirySeconds(String(seconds));
    };
    const handleSetAmount = (e) => {
      setAmountInput(e.detail.amount.toString());
    };
    window.addEventListener('set-ez5050-time', handleSetTime);
    window.addEventListener('set-ez5050-amount', handleSetAmount);
    return () => {
      window.removeEventListener('set-ez5050-time', handleSetTime);
      window.removeEventListener('set-ez5050-amount', handleSetAmount);
    };
  }, []);
  const [latestOrder, setLatestOrder] = useState(restoredOrder);
  const [activeOrders, setActiveOrders] = useState([]);
  const [resultModal, setResultModal] = useState({
    isOpen: false,
    order: null,
  });
  const [activeResultLayoutRef, setActiveResultLayoutRef] = useState(
    layoutRef ?? null,
  );
  const [luckyModal, setLuckyModal] = useState({
    isOpen: false,
    data: null,
  });

  const [userRank, setUserRank] = useState("basic");
  const [dynamicExpiryOptions, setDynamicExpiryOptions] =
    useState(EXPIRY_OPTIONS);
  const [dynamicAmountOptions, setDynamicAmountOptions] = useState(
    AMOUNT_PICKER_OPTIONS,
  );
  const [dynamicPayoutPercent, setDynamicPayoutPercent] =
    useState(FIXED_PAYOUT_PERCENT);
  const [dynamicPayoutMultiplier, setDynamicPayoutMultiplier] = useState(
    FIXED_PAYOUT_MULTIPLIER,
  );

  const effectivePayoutPercent = useMemo(() => {
    const fromWallet = Number(wallet?.binary?.win_percentage);
    if (Number.isFinite(fromWallet) && fromWallet > 0) return fromWallet;
    if (Number.isFinite(dynamicPayoutPercent) && dynamicPayoutPercent > 0) return dynamicPayoutPercent;
    return FIXED_PAYOUT_PERCENT;
  }, [wallet?.binary?.win_percentage, dynamicPayoutPercent]);

  const effectivePayoutMultiplier = useMemo(() => {
    return 1 + effectivePayoutPercent / 100;
  }, [effectivePayoutPercent]);

  const [resultModalQueue, setResultModalQueue] = useState([]);
  const [showJackpot, setShowJackpot] = useState(true);
  const seenSpinCountRef = useRef(null);

  useEffect(() => {
    if (wallet?.hasSpin?.spinCount !== undefined && seenSpinCountRef.current === null) {
      seenSpinCountRef.current = Number(wallet.hasSpin.spinCount || 0);
    }
  }, [wallet]);
  const [recentResults, setRecentResults] = useState([]);
  const rememberedPairOptionsRef = useRef(new Map());

  const handlePairTypeChange = (type) => {
    setPairType(type);
  };

  const getRecentResults = async () => {
    try {
      const { data } = await Axios.post(`/binaryOptions/recentUpDownResults`, {
        pair: currentPairRoom,
      });
      if (data?.success) {
        setRecentResults(data?.result || []);
      } else {
        setRecentResults([]);
      }
    } catch (e) {
      setRecentResults([]);
      console.error("Error fetching recent results:", e);
    }
  };
  const [roundBidsRefreshTick, setRoundBidsRefreshTick] = useState(0);
  const variant = useMemo(
    () =>
      ROUND_VARIANTS.find((option) => option.id === variantId) ??
      ROUND_VARIANTS[0],
    [variantId],
  );

  const pairOptions = useMemo(() => {
    if (pairs.length > 0) {
      return pairs;
    }
    return [];
  }, [pairs]);

  useEffect(() => {
    pairOptions.forEach((pair) => {
      if (pair?.symbol) {
        rememberedPairOptionsRef.current.set(pair.symbol, pair);
      }
    });
  }, [pairOptions]);


  const listedActivePairOption = useMemo(
    () => pairOptions.find((pair) => pair.symbol === selectedPair),
    [pairOptions, selectedPair],
  );

  const activePairOption = useMemo(() => {
    if (listedActivePairOption) {
      return listedActivePairOption;
    }

    const cachedPairOption = selectedPair
      ? rememberedPairOptionsRef.current.get(selectedPair)
      : null;
    if (cachedPairOption) {
      return cachedPairOption;
    }

    if (selectedPair) {
      return {
        symbol: selectedPair,
        label: formatPairLabelFromSymbol(selectedPair),
      };
    }

    return null;
  }, [listedActivePairOption, selectedPair]);
  const activePairLabel = activePairOption?.label ?? symbol;
  const activePairLabelRef = useRef(activePairLabel);
  activePairLabelRef.current = activePairLabel;
  const currentPairRoom = useMemo(
    () => toBackendPairSymbol(selectedPair ?? marketSymbol, activePairLabel),
    [activePairLabel, marketSymbol, selectedPair],
  );
  const selectedExpiryOption = useMemo(
    () =>
      dynamicExpiryOptions.find((option) => option.id === selectedExpiryId) ??
      dynamicExpiryOptions[0],
    [selectedExpiryId, dynamicExpiryOptions],
  );
  const isCustomExpirySelected = selectedExpiryId === CUSTOM_EXPIRY_ID;
  const normalizedCustomExpirySeconds = useMemo(
    () =>
      clampCustomExpirySeconds(
        customExpirySeconds,
        DEFAULT_ORDER_DURATION_SECONDS,
      ),
    [customExpirySeconds],
  );
  const selectedExpirySeconds = useMemo(
    () =>
      isCustomExpirySelected
        ? normalizedCustomExpirySeconds
        : (selectedExpiryOption?.seconds ?? DEFAULT_ORDER_DURATION_SECONDS),
    [
      isCustomExpirySelected,
      normalizedCustomExpirySeconds,
      selectedExpiryOption,
    ],
  );
  const effectiveVariant = variant;

  const amount = useMemo(() => parseStake(amountInput), [amountInput]);
  const isTradeOpen = phase === "trading";
  const currentRoundId = String(roundDetails?.roundId || "").trim();
  const symbolActiveOrdersCount = useMemo(() => {
    const currentPair = (activePairLabel || "").replace("_", "/").toUpperCase();
    const now = Date.now();
    return (activeOrders || []).filter((o) => {
      const pairMatches = (o.pair || "").replace("_", "/").toUpperCase() === currentPair;
      if (!pairMatches) return false;
      const status = (o.status || "").toLowerCase();
      if (["won", "loss", "lost", "draw", "settled", "completed"].includes(status)) {
        return false;
      }
      if (o.endTime && new Date(o.endTime).getTime() <= now) {
        return false;
      }
      if (o.expiresAtMs && o.expiresAtMs <= now) {
        return false;
      }
      return status === "active";
    }).length;
  }, [activeOrders, activePairLabel]);

  const {
    areTradeParticipantsVisible,
    setAreTradeParticipantsVisible,
    isTradePanelClosing,
    setIsTradePanelClosing,
    tradeParticipants,
    setTradeParticipants,
    shouldShowTradeParticipants,
    displayBets,
    pools,
    upPotentialReturn,
    downPotentialReturn,
    sideUpPlayers,
    sideDownPlayers,
    sidePools,
    mobileUpPlayers,
    mobileDownPlayers,
    compactUpPlayers,
    compactDownPlayers,
    resetParticipants,
  } = useUpDownParticipants({
    latestOrder,
    variant,
    variantId,
    amount,
    effectivePayoutMultiplier,
  });

  const isTradeActionDisabled = !isTradeOpen || symbolActiveOrdersCount >= 3;

  const upPayoutPercent = effectivePayoutPercent;
  const downPayoutPercent = effectivePayoutPercent;

    const settledWinningSide = useMemo(() => {
    if (!latestOrder || latestOrder.status === "active") {
      return null;
    }
    if (latestOrder.side === "up") {
      return latestOrder.won ? "up" : "down";
    }
    return latestOrder.won ? "down" : "up";
  }, [latestOrder]);

  const currentOpenPosition = useMemo(() => {
    if (!Array.isArray(activeOrders) || activeOrders.length === 0) {
      return null;
    }
    return (
      activeOrders.find((order) => order.status === "active") ?? activeOrders[0]
    );
  }, [activeOrders]);

  const [criticalWinState, setCriticalWinState] = useState({
    isOpen: false,
    amount: "0.00",
    streak: 3,
  });
  const lastCriticalWinTimeRef = useRef(0);

  useEffect(() => {
    const handleCriticalWin = (e) => {
      const now = Date.now();
      if (now - lastCriticalWinTimeRef.current < 1500) return;
      lastCriticalWinTimeRef.current = now;

      const amount = e.detail?.amount ?? "0.00";
      const streak = e.detail?.streak ?? 3;

      if (typeof document !== "undefined") {
        document.body.classList.remove("camera-shake");
        void document.body.offsetWidth;
        document.body.classList.add("camera-shake");
        setTimeout(() => {
          document.body.classList.remove("camera-shake");
        }, 350);
      }

      setCriticalWinState({
        isOpen: true,
        amount: typeof amount === "number" ? amount.toFixed(2) : String(amount),
        streak: Number(streak) || 3,
      });
    };

    window.addEventListener("ez5050-critical-win", handleCriticalWin);
    return () => {
      window.removeEventListener("ez5050-critical-win", handleCriticalWin);
    };
  }, []);

  const [settlementAura, setSettlementAura] = useState(null);
  const settlementAuraTimerRef = useRef(null);

  const triggerSettlementAura = useCallback((outcome) => {
    const isWin = outcome === "won" || outcome === "win" || outcome === "WON" || outcome === "WIN";
    const status = isWin ? "won" : "loss";
    setSettlementAura(status);

    if (status === "loss" && typeof document !== "undefined") {
      document.body.classList.remove("camera-shake");
      void document.body.offsetWidth;
      document.body.classList.add("camera-shake");
      setTimeout(() => {
        document.body.classList.remove("camera-shake");
      }, 350);
    }

    if (settlementAuraTimerRef.current) {
      clearTimeout(settlementAuraTimerRef.current);
    }
    settlementAuraTimerRef.current = setTimeout(() => {
      setSettlementAura(null);
    }, 2600);
  }, []);

  useEffect(() => {
    const handleSettlementEvent = (e) => {
      if (e.detail?.outcome) {
        triggerSettlementAura(e.detail.outcome);
      }
    };
    window.addEventListener("ez5050-settlement-aura", handleSettlementEvent);
    return () => {
      window.removeEventListener("ez5050-settlement-aura", handleSettlementEvent);
      if (settlementAuraTimerRef.current) {
        clearTimeout(settlementAuraTimerRef.current);
      }
    };
  }, [triggerSettlementAura]);



  const phaseDurationMs = useMemo(() => {
    if (phase === "trading") {
      return variant.tradeWindowSeconds * 1000;
    }
    if (phase === "locked") {
      return variant.lockWindowSeconds * 1000;
    }
    if (phase === "settled") {
      return SETTLEMENT_PHASE_MS;
    }
    return PAYOUT_PHASE_MS;
  }, [phase, variant.lockWindowSeconds, variant.tradeWindowSeconds]);

  const remainingSeconds = useMemo(
    () => Math.max(0, Math.ceil((phaseEndsAt - clockNow) / 1000)),
    [phaseEndsAt, clockNow],
  );

  const chartForwardSpaceBars = useMemo(() => {
    // Fixed margin so the chart stays perfectly still when selecting 1m, 2s, etc.
    return 15;
  }, []);

  const handleChartTimeframeAutoChange = useCallback(
    (nextTimeframe) => {
      if (
        !CHART_INTERVALS.includes(nextTimeframe) ||
        nextTimeframe === timeframe
      ) {
        return;
      }
      onTimeframeChange(nextTimeframe);
    },
    [onTimeframeChange, timeframe],
  );

  const middleTimerSeconds = useMemo(
    () => selectedExpirySeconds,
    [selectedExpirySeconds],
  );

  const phaseProgress = useMemo(() => {
    const elapsed = clockNow - phaseStartedAt;
    return clamp(elapsed / Math.max(phaseDurationMs, 1), 0, 1);
  }, [clockNow, phaseStartedAt, phaseDurationMs]);

  const raceTrackProgress = useMemo(() => {
    const tradingMs = variant.tradeWindowSeconds * 1000;
    const lockedMs = variant.lockWindowSeconds * 1000;
    const settledMs = SETTLEMENT_PHASE_MS;
    const payoutMs = PAYOUT_PHASE_MS;
    const totalMs = Math.max(1, tradingMs + lockedMs + settledMs + payoutMs);

    if (phase === "trading") {
      return (phaseProgress * tradingMs) / totalMs;
    }

    if (phase === "locked") {
      return (tradingMs + phaseProgress * lockedMs) / totalMs;
    }

    if (phase === "settled") {
      return (tradingMs + lockedMs + phaseProgress * settledMs) / totalMs;
    }

    return (
      (tradingMs + lockedMs + settledMs + phaseProgress * payoutMs) / totalMs
    );
  }, [
    phase,
    phaseProgress,
    variant.lockWindowSeconds,
    variant.tradeWindowSeconds,
  ]);

  const liveMarkerPercent = Number.isFinite(liveMarkerXPercentRef.current)
    ? liveMarkerXPercentRef.current
    : LIVE_MARKER_FALLBACK_PERCENT;

  const closeResultModal = useCallback(() => {
    setResultModal({ isOpen: false, order: null });
  }, [wallet, setResultModal]);

  useEffect(() => {
    setActiveResultLayoutRef(layoutRef ?? null);
  }, [layoutRef]);

  const raceTimerLeadPercent =
    RACE_TIMER_LEAD_START_PERCENT +
    (RACE_TIMER_LEAD_END_PERCENT - RACE_TIMER_LEAD_START_PERCENT) *
      raceTrackProgress;

  const raceTimerPercent = clamp(
    liveMarkerPercent + raceTimerLeadPercent,
    0,
    100,
  );
  const raceFlagPercent = clamp(
    raceTimerPercent + RACE_FLAG_GAP_PERCENT,
    0,
    100,
  );

  const recentOutcomeSlots = useMemo(() => {
    const latest = recentOutcomes.slice(-OUTCOME_SLOT_COUNT);
    if (latest.length >= OUTCOME_SLOT_COUNT) {
      return latest;
    }
    return [...Array(OUTCOME_SLOT_COUNT - latest.length).fill(null), ...latest];
  }, [recentOutcomes]);

  useEffect(() => {
    startingPriceRef.current = startingPrice;
    if (Number.isFinite(startingPrice) && !hasAlignedInitialRoundRef.current) {
      setRoundStartPrice(startingPrice);
      setLivePrice(startingPrice);
    }
  }, [startingPrice]);

  useEffect(() => {
    variantRef.current = variant;
  }, [variant]);

  useEffect(() => {
    roundIndexRef.current = roundIndex;
  }, [roundIndex]);

  useEffect(() => {
    betsRef.current = bets;
  }, [bets]);

  // Old modal logic removed for RPG floating text

  useEffect(() => {
    return () => {
      localResultOrderIdsRef.current.forEach((orderId) => {
        forgetResultOwner(orderId);
      });
      localResultOrderIdsRef.current.clear();
    };
  }, []);

  const closeLuckyModal = () => {
    if (luckyData) {
      const comboKey = luckyData.earnedAtTradeId
        ? `combo_${luckyData.multiplier}_${luckyData.earnedAtTradeId}`
        : luckyData.matchedPattern
          ? `combo_${luckyData.multiplier}_${luckyData.matchedPattern}`
          : `combo_${luckyData.multiplier}_${luckyData.source || 'active'}`;
      try {
        sessionStorage.setItem("seen_lucky_combo_key", comboKey);
      } catch (_e) {}
    }
    setLuckyModal({ isOpen: false, data: null });
    setShowJackpot(true);
  };

  useEffect(() => {
    if (luckyData?.multiplier > 1 && luckyData?.isLucky) {
      const comboKey = luckyData.earnedAtTradeId
        ? `combo_${luckyData.multiplier}_${luckyData.earnedAtTradeId}`
        : luckyData.matchedPattern
          ? `combo_${luckyData.multiplier}_${luckyData.matchedPattern}`
          : `combo_${luckyData.multiplier}_${luckyData.source || 'active'}`;
      let hasSeen = false;
      try {
        hasSeen = sessionStorage.getItem("seen_lucky_combo_key") === comboKey;
      } catch (_e) {}

      // Open modal only if user has not yet seen this specific combo
      if (!hasSeen) {
        setLuckyModal({ isOpen: true, data: luckyData });
      }
      justSettledTradeRef.current = false;
      setShowJackpot(true);
    } else {
      setLuckyModal({ isOpen: false, data: null });
      setShowJackpot(true);
      justSettledTradeRef.current = false;
    }
  }, [luckyData]);

  useEffect(() => {
    if (wallet?.binary?.activeTrades?.length > 0) {


      setActiveOrders((prevActiveOrders) => {
        const newActiveOrders =
          wallet?.binary?.activeTrades
            ?.filter((t) => {
              const id = String(t.id || t.order_id || "");
              if (settledOrderIdsRef.current.has(id)) return false;
              if (ignoredBackendTradeIdsRef.current.has(id)) return false;
              const pairMatches = (t.pair || "").replace("_", "/") === activePairLabelRef.current;
              return pairMatches;
            })
            .map((t) => {
              const rawMult = Number(t.payoutMultiplier ?? t.profitPercent ?? t.multiplier ?? 0);
              const normalizedMult = rawMult > 10 ? 1 + rawMult / 100 : (rawMult > 0 ? rawMult : FIXED_PAYOUT_MULTIPLIER);
              return {
                ...t,
                id: String(t.id || t.order_id || ""),
                side: t.side || String(t.direction || "up").toLowerCase(),
                pair: (t.pair || "").replace("_", "/"),
                status: t.status || "active",
                payoutMultiplier: normalizedMult,
              };
            })
            .sort((a, b) => {
              const timeA = new Date(a.startTime || a.entryTime || 0).getTime();
              const timeB = new Date(b.startTime || b.entryTime || 0).getTime();
              return timeB - timeA;
            }) || [];
            
        const optimisticOrders = prevActiveOrders.filter(
          (o) => String(o.id).startsWith("order-") && !settledOrderIdsRef.current.has(o.id)
        );
        
        const mergedMap = new Map();
        optimisticOrders.forEach(o => mergedMap.set(o.id, o));
        newActiveOrders.forEach(o => mergedMap.set(o.id, o));
        const merged = Array.from(mergedMap.values()).sort((a, b) => {
          const timeA = new Date(a.startTime || a.entryTime || 0).getTime();
          const timeB = new Date(b.startTime || b.entryTime || 0).getTime();
          return timeB - timeA;
        });

        if (
          JSON.stringify(prevActiveOrders) !==
          JSON.stringify(merged)
        ) {
          activeOrdersDataRef.current = merged; // Safe to do here, but comparing to prevActiveOrders prevents the strict mode bug

          return merged;
        }

        return prevActiveOrders;
      });

      // NOTE: Logging activeOrders right after setActiveOrders will always show the OLD value 
      // because React updates state asynchronously. 

    }
    if (wallet?.binary?.time_settings?.length > 0) {
      const mappedSettings = wallet.binary.time_settings.map(s => ({
        ...s,
        id: s.id || s._id || `custom_${s.seconds}`,
        value: s.value || `${Math.floor(s.seconds / 60)}:${(s.seconds % 60).toString().padStart(2, '0')}`
      })).sort((a, b) => Number(a.seconds) - Number(b.seconds));
      if (
        JSON.stringify(dynamicExpiryOptions) !==
        JSON.stringify(mappedSettings)
      ) {
        setDynamicExpiryOptions(mappedSettings);
      }
    }
    if (wallet?.binary?.amount_settings?.length > 0) {
      const apiAmounts = wallet.binary.amount_settings.map((s) => Number(s.amount)).sort((a, b) => a - b);
      if (
        JSON.stringify(dynamicAmountOptions) !== JSON.stringify(apiAmounts)
      ) {
        setDynamicAmountOptions(apiAmounts);
      }
    }
  }, [wallet, symbol, marketSymbol]);

  useEffect(() => {
    let rafId = 0;
    let isCancelled = false;

    const frame = () => {
      if (isCancelled) {
        return;
      }

      const now = Date.now();

      if (now - livePricePollAtRef.current >= 90) {
        const latestPrice = chartRef.current?.getCurrentPrice?.();
        if (Number.isFinite(latestPrice)) {
          setLivePrice(latestPrice);
          if (
            !hasAlignedInitialRoundRef.current &&
            roundIndexRef.current <= 1
          ) {
            setRoundStartPrice(latestPrice);
            hasAlignedInitialRoundRef.current = true;
          }
        }
        const markerPercent = chartRef.current?.getLiveMarkerPercentX?.();
        if (Number.isFinite(markerPercent)) {
          liveMarkerXPercentRef.current = clamp(markerPercent, 0, 100);
        }
        livePricePollAtRef.current = now;
      }

      if (now - clockPaintAtRef.current >= 33) {
        setClockNow(now);
        clockPaintAtRef.current = now;
      }

      rafId = requestAnimationFrame(frame);
    };

    rafId = requestAnimationFrame(frame);

    return () => {
      isCancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, []);

  useEffect(() => {
    if (activeOrders === undefined || activeOrders?.length === 0) {
      if (latestOrder) {
        setLatestOrder(null);
      }
      return;
    }

    const hasCurrentLatestOrder = latestOrder
      ? activeOrders?.some((order) => order.id === latestOrder.id)
      : false;

    if (!hasCurrentLatestOrder) {
      setLatestOrder(activeOrders[activeOrders?.length - 1] ?? null);
    }
  }, [activeOrders, latestOrder]);

  // ÃƒÂ°Ã…Â¸Ã¢â‚¬ÂÃ‚Â§ Trade marker persistence and synchronization is now handled reactively
  // by passing [activeOrders] prop directly to TradingChart.
  // useEffect(() => {
  //   if (!chartRef.current?.placeOrder) {
  //     return;
  //   }
  //
  //   activeOrders?.forEach((order) => { ... });
  // }, [activeOrders]);

  // ÃƒÂ°Ã…Â¸Ã¢â‚¬ÂÃ‚Â§ FIX: Removed useEffect that was saving activeOrders to localStorage

  useEffect(
    () => () => {
      if (tradeParticipantsRevealTimerRef.current !== null) {
        clearTimeout(tradeParticipantsRevealTimerRef.current);
        tradeParticipantsRevealTimerRef.current = null;
      }
      if (tradePanelCloseTimerRef.current !== null) {
        clearTimeout(tradePanelCloseTimerRef.current);
        tradePanelCloseTimerRef.current = null;
      }
      if (tradePanelFinalizeTimerRef.current !== null) {
        clearTimeout(tradePanelFinalizeTimerRef.current);
        tradePanelFinalizeTimerRef.current = null;
      }
      orderRemovalTimersRef.current.forEach((timerId) => clearTimeout(timerId));
      orderRemovalTimersRef.current.clear();
    },
    [],
  );

  useEffect(
    () => () => {
      if (joinKickoffTimerRef.current !== null) {
        clearTimeout(joinKickoffTimerRef.current);
      }
      if (joinNextTimerRef.current !== null) {
        clearTimeout(joinNextTimerRef.current);
      }
      if (pairRoundResetTimerRef.current !== null) {
        clearTimeout(pairRoundResetTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (!isPairBootstrappedRef.current) {
      isPairBootstrappedRef.current = true;
      hasAlignedInitialRoundRef.current = false;
      // ÃƒÂ°Ã…Â¸Ã¢â‚¬ÂÃ‚Â§ FIX: Removed logic that restores session state from localStorage
      return;
    }

    hasAlignedInitialRoundRef.current = false;
    setCurrentRoundBids([]);
    setBets([]);
    setSettlement(null);
    setActiveOrders([]);
    setLatestOrder(null);
    resetParticipants();
    setPhase("trading");
  }, [marketSymbol]);

  const fetchRankAndSettings = useCallback(async () => {
    try {
      // 1. Get User Profile to get Rank
      const { data: profileRes } = await Axios.post("/users/profile");
      let currentRank = "basic";
      if (profileRes?.success) {
        currentRank = profileRes?.result?.rank || "basic";
        setUserRank(currentRank);
      }

      // 2. Get Trade Settings for this rank
      const { data: settingsRes } = await Axios.post("/get-trade-settings", {
        type: "binary",
        role: currentRank,
      });
      if (settingsRes?.success) {
        const settings = settingsRes.result[0] || settingsRes.result;

        // Filter Time Settings
        if (settings?.binary?.time_settings) {
          const filteredTimes = settings.binary.time_settings
            .filter((t) => t.role === currentRank && t.isActive)
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((t) => ({
              id: t.label || `${t.seconds}s`,
              seconds: t.seconds,
              label: t.label,
              value:
                t.value ||
                `${Math.floor(t.seconds / 60)}:${String(t.seconds % 60).padStart(2, "0")}`,
            }));
          if (filteredTimes.length > 0) {
            setDynamicExpiryOptions(filteredTimes);
            // Auto-select first if:
            // 1. This is the very first load of settings
            // 2. OR the current selected ID is no longer valid in the new options list
            if (isFirstSettingsLoadRef.current) {
              setSelectedExpiryId(filteredTimes[0].id);
              setTimerInput(filteredTimes[0].value);
              isFirstSettingsLoadRef.current = false;
            } else if (!filteredTimes.some((o) => o.id === selectedExpiryId)) {
              setSelectedExpiryId(filteredTimes[0].id);
              setTimerInput(filteredTimes[0].value);
            }
          }
        }

        // Filter Amount Settings
        if (settings?.binary?.amount_settings) {
          const filteredAmounts = settings.binary.amount_settings
            .filter((a) => a.role === currentRank && a.isActive)
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((a) => a.amount);
          if (filteredAmounts.length > 0) {
            setDynamicAmountOptions(filteredAmounts);
            // Default to the first amount (index 0) immediately on first load
            if (!amountInput || amountInput === "") {
              setAmountInput(filteredAmounts[0].toFixed(2));
            }
          }
        }

        // Determine Payout Percent
        let payoutPercent = 0;

        // A. Priority: Check if there's a global profit percentage for this rank in roles settings
        const roleSettings = settings[currentRank] || {};
        if (Number(roleSettings.win_percentage) > 0) {
          payoutPercent = Number(roleSettings.win_percentage);
        }

        // B. Fallback: Calculate from Admin Commission
        if (!payoutPercent) {
          const commission = Number(roleSettings.admin_commission) || 10;
          payoutPercent = 100 - commission;
        }

        setDynamicPayoutPercent(payoutPercent);
        setDynamicPayoutMultiplier(1 + payoutPercent / 100);
      }
    } catch (error) {
      console.error("Error fetching rank/settings:", error);
    }
  }, []);

  const getWallet = useCallback(async () => {
    try {
      const { data } = await Axios.post("/users/getWallet", {});
      if (data?.success) {
        const hasSpinData = data.result?.spin_and_win?.hasSpin;
        setWallet({ ...data?.result, hasSpin: hasSpinData });
        if (seenSpinCountRef.current === null && hasSpinData?.spinCount !== undefined) {
          seenSpinCountRef.current = Number(hasSpinData.spinCount);
        }
        // Only update activeOrders if data actually changed (prevent unnecessary re-renders)
        setActiveOrders((prevActiveOrders) => {
          const rawActiveTrades = data?.result?.binary?.activeTrades || [];



          const newActiveOrders =
            rawActiveTrades
              ?.filter((t) => {
                const id = String(t.id || t.order_id || "");
                if (settledOrderIdsRef.current.has(id)) return false;
                if (ignoredBackendTradeIdsRef.current.has(id)) return false;
                
                const pairMatches = (t.pair || "").replace("_", "/") === activePairLabelRef.current;
                return pairMatches;
              })
              .map((t) => {
                const rawMult = Number(t.payoutMultiplier ?? t.profitPercent ?? t.multiplier ?? 0);
                const normalizedMult = rawMult > 10 ? 1 + rawMult / 100 : (rawMult > 0 ? rawMult : FIXED_PAYOUT_MULTIPLIER);
                return {
                  ...t,
                  id: String(t.id || t.order_id || ""),
                  side: t.side || String(t.direction || "up").toLowerCase(),
                  pair: (t.pair || "").replace("_", "/"),
                  status: t.status || "active",
                  payoutMultiplier: normalizedMult,
                };
              })
              .sort((a, b) => {
                const timeA = new Date(a.startTime || a.entryTime || 0).getTime();
                const timeB = new Date(b.startTime || b.entryTime || 0).getTime();
                return timeB - timeA;
              }) || [];

          const optimisticOrders = prevActiveOrders.filter(
            (o) => String(o.id).startsWith("order-") && !settledOrderIdsRef.current.has(o.id)
          );

          const mergedMap = new Map();
          optimisticOrders.forEach(o => mergedMap.set(o.id, o));
          newActiveOrders.forEach(o => mergedMap.set(o.id, o));
          const merged = Array.from(mergedMap.values()).sort((a, b) => {
            const timeA = new Date(a.startTime || a.entryTime || 0).getTime();
            const timeB = new Date(b.startTime || b.entryTime || 0).getTime();
            return timeB - timeA;
          });

          if (
            JSON.stringify(prevActiveOrders) !==
            JSON.stringify(merged)
          ) {
            activeOrdersDataRef.current = merged;
            return merged;
          }
          return prevActiveOrders;
        });
      } else {
        setWallet({ balance: 0 });
      }
    } catch (error) {
      setWallet({ balance: 0 });
      console.error("Error fetching wallet:", error);
    }
  }, []);

  // ── Safety Watchdog: clean up orphaned expired orders if backend socket completely failed ──
  useEffect(() => {
    if (!activeOrders || activeOrders.length === 0) return;

    const GRACE_MS = 15000; // wait 15s past expiry before cleaning up orphaned trades
    const CHECK_INTERVAL_MS = 1000;

    const intervalId = setInterval(() => {
      const now = Date.now();
      activeOrders.forEach((order) => {
        if (order.status !== "active" && order.status !== "settling") return;
        const endMs = order.expiresAtMs || new Date(order.endTime).getTime();
        if (!Number.isFinite(endMs) || endMs <= 0) return;
        if (now < endMs + GRACE_MS) return;

        // This order has expired long ago and backend never sent socket event
        console.warn(`[safety] Cleaning up orphaned expired order ${order.id}`);

        // Mark settled in the set so getWallet doesn't re-add it
        settledOrderIdsRef.current.add(order.id);
        setTimeout(() => settledOrderIdsRef.current.delete(order.id), 30000);

        // Remove from activeOrders and chart cleanly
        setActiveOrders((prev) => prev?.filter((o) => o.id !== order.id));
        setLatestOrder((prev) => (prev?.id === order.id ? null : prev));
        chartRef.current?.removeOrder?.(order.id);
        chartOrderHydrationRef.current.delete(order.id);

        // Sync authoritative state from server
        getWallet();
        if (typeof getLuckyRoundDetails === "function") {
          getLuckyRoundDetails();
        }
      });
    }, CHECK_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [activeOrders, getWallet, getLuckyRoundDetails]);

  const [profitDetails, setProfitDetails] = useState({
    upProfitPercent: 0,
    downProfitPercent: 0,
  });
  // useEffect(() => {
  //   const userBid = currentRoundBids.find(
  //     (bid) =>
  //       String(bid?.roundId || '').trim() === currentRoundId &&
  //       Boolean(bid?.user?.isUser)
  //   );

  //   const ADMIN_COMMISSION = roundDetails?.stats?.adminCommission || 10;
  //   const commissionRate = 1 - ADMIN_COMMISSION / 100;

  //   const inputAmount = Number(amountInput) || 0;

  //   const baseUpAmount = Number(roundDetails?.stats?.upAmount || 0);
  //   const baseDownAmount = Number(roundDetails?.stats?.downAmount || 0);

  //   // ---------- ÃƒÂ°Ã…Â¸Ã…Â¸Ã‚Â¢ PREVIEW MODE ----------
  //   if (!userBid) {
  //     let upReturn = 0;
  //     let downReturn = 0;
  //     let upProfitPercent = 0;
  //     let downProfitPercent = 0;

  //     // ÃƒÂ°Ã…Â¸Ã¢â‚¬ËœÃ¢â‚¬Â° Simulate UP
  //     if (inputAmount > 0) {
  //       const simulatedUpAmount = baseUpAmount + inputAmount;

  //       if (simulatedUpAmount > 0 && baseDownAmount > 0) {
  //         const downPool = baseDownAmount * commissionRate;

  //         upReturn =
  //           inputAmount +
  //           (inputAmount / simulatedUpAmount) * downPool;

  //         const profit = upReturn - inputAmount;
  //         upProfitPercent = (profit / inputAmount) * 100;
  //       }
  //     }

  //     // ÃƒÂ°Ã…Â¸Ã¢â‚¬ËœÃ¢â‚¬Â° Simulate DOWN
  //     if (inputAmount > 0) {
  //       const simulatedDownAmount = baseDownAmount + inputAmount;

  //       if (simulatedDownAmount > 0 && baseUpAmount > 0) {
  //         const upPool = baseUpAmount * commissionRate;

  //         downReturn =
  //           inputAmount +
  //           (inputAmount / simulatedDownAmount) * upPool;

  //         const profit = downReturn - inputAmount;
  //         downProfitPercent = (profit / inputAmount) * 100;
  //       }
  //     }

  //     setProfitDetails({
  //       upReturn: Number(upReturn.toFixed(2)) || 0,
  //       downReturn: Number(downReturn.toFixed(2)) || 0,
  //       upProfitPercent: Number(upProfitPercent.toFixed(2)) || 0,
  //       downProfitPercent: Number(downProfitPercent.toFixed(2)) || 0
  //     });

  //     return;
  //   }

  //   // ---------- ÃƒÂ°Ã…Â¸Ã¢â‚¬ÂÃ‚Âµ USER ALREADY BID ----------
  //   const bidAmount = Number(userBid.amount);
  //   const direction = userBid.direction;

  //   let upReturn = 0;
  //   let downReturn = 0;
  //   let upProfitPercent = 0;
  //   let downProfitPercent = 0;

  //   if (direction === 'UP') {
  //     if (baseUpAmount > 0 && baseDownAmount > 0) {
  //       const downPool = baseDownAmount * commissionRate;

  //       upReturn =
  //         bidAmount +
  //         (bidAmount / baseUpAmount) * downPool;

  //       const profit = upReturn - bidAmount;
  //       upProfitPercent = (profit / bidAmount) * 100;
  //     }
  //   }

  //   if (direction === 'DOWN') {
  //     if (baseDownAmount > 0 && baseUpAmount > 0) {
  //       const upPool = baseUpAmount * commissionRate;

  //       downReturn =
  //         bidAmount +
  //         (bidAmount / baseDownAmount) * upPool;

  //       const profit = downReturn - bidAmount;
  //       downProfitPercent = (profit / bidAmount) * 100;
  //     }
  //   }

  //   setProfitDetails({
  //     upReturn: direction === 'UP' ? Number(upReturn.toFixed(2)) : 0,
  //     downReturn: direction === 'DOWN' ? Number(downReturn.toFixed(2)) : 0,
  //     upProfitPercent: direction === 'UP' ? Number(upProfitPercent.toFixed(2)) : 0,
  //     downProfitPercent: direction === 'DOWN' ? Number(downProfitPercent.toFixed(2)) : 0
  //   });

  // }, [roundDetails, amountInput, currentRoundBids, currentRoundId]);
  useEffect(() => {
    const previewAmount = parseStake(amountInput);
    const safeAmount = Number.isFinite(previewAmount) ? previewAmount : 0;
    setProfitDetails({
      upReturn: roundToCents(safeAmount * effectivePayoutMultiplier),
      downReturn: roundToCents(safeAmount * effectivePayoutMultiplier),
      upProfitPercent: effectivePayoutPercent,
      downProfitPercent: effectivePayoutPercent,
    });
  }, [amountInput, effectivePayoutMultiplier, effectivePayoutPercent]);

  const applyRoundBidsToPanel = (bidsResponse, nextRoundId, pairRoom) => {
    const responseBids = Array.isArray(bidsResponse?.result)
      ? bidsResponse.result
      : [];
    const normalizedPair = String(pairRoom || "").toUpperCase();
    const mappedServerBids = responseBids
      .filter((bid) => {
        const bidPair = String(bid?.pair || "").toUpperCase();
        return !bidPair || bidPair === normalizedPair;
      })
      .map((bid, index) => {
        const direction = String(bid?.direction || "").toUpperCase();
        if (direction !== "UP" && direction !== "DOWN") {
          return null;
        }

        const amountValue = Number(bid?.amount);
        const amount = Number.isFinite(amountValue) ? amountValue : 0;
        const userName = String(bid?.user?.name || "").trim() || "You";
        const profileImage = String(bid?.user?.profile_image || "").trim();
        const bidId = String(bid?.bidId || "").trim();
        const isCurrentUser = Boolean(bid?.user?.isUser);

        return {
          id: bidId || `server-bid-${nextRoundId}-${index}`,
          roundId: String(bid?.roundId || nextRoundId),
          side: direction === "UP" ? "up" : "down",
          amount,
          name: userName,
          profileImage,
          isUser: isCurrentUser,
          isServerBid: true,
        };
      })
      .filter(Boolean);
    setCurrentRoundBids(responseBids);
    setBets((previous) => {
      const houseOnly = previous.filter(
        (bet) => !bet.isServerBid && !bet.isPendingUserBid,
      );
      return [...houseOnly, ...mappedServerBids];
    });

    let upAmount = 0;
    let downAmount = 0;
    responseBids.forEach((bid) => {
      const direction = String(bid?.direction || "").toUpperCase();
      const amountValue = Number(bid?.amount);
      if (!Number.isFinite(amountValue)) return;
      if (direction === "UP") upAmount += amountValue;
      if (direction === "DOWN") downAmount += amountValue;
    });
    const adminCommission =
      bidsResponse?.stats &&
      typeof bidsResponse.stats.adminCommission === "number"
        ? bidsResponse.stats.adminCommission
        : 10;
    setRoundDetails((prev) => ({
      ...prev,
      stats: {
        ...(prev.stats || {}),
        upAmount,
        downAmount,
        adminCommission,
      },
    }));

    // Keep only house entries queued for join animation; user bids now come from server.
    joinBatchRef.current = joinBatchRef.current.filter((bet) => !bet?.isUser);
  };

  const getCurrentRoundBids = async () => {
    const nextRoundId = String(roundDetails?.roundId || "").trim();
    const pairRoom = String(roundDetails?.pair || currentPairRoom || "").trim();
    if (!nextRoundId || !pairRoom) {
      return;
    }

    try {
      const { data } = await Axios.get("/binaryOptions/round-bids", {
        params: {
          roundId: nextRoundId,
          pair: pairRoom,
          limit: 30,
        },
      });
      if (data?.success) {
        // ...existing code...
        applyRoundBidsToPanel(data, nextRoundId, pairRoom);
      } else {
        setCurrentRoundBids([]);
      }
    } catch (error) {
      setCurrentRoundBids([]);
      // ...existing code...
    }
  };

  useEffect(() => {
    getCurrentRoundBids();
    fetchRankAndSettings();
    // getRecentResults();
  }, [
    roundDetails.roundId,
    roundDetails.pair,
    currentPairRoom,
    roundBidsRefreshTick,
    fetchRankAndSettings,
  ]);

  // Map recentResults to recentOutcomes for outcome pills
  useEffect(() => {
    if (Array.isArray(recentResults)) {
      // Map API 'result' field: 'UP' -> 'up', 'DOWN' -> 'down', others (CANCELLED, etc) -> null
      const mapped = recentResults.map((item) => {
        if (item.result === "UP") return "up";
        if (item.result === "DOWN") return "down";
        return null;
      });
      setRecentOutcomes(mapped.slice(-OUTCOME_SLOT_COUNT));
    } else {
      setRecentOutcomes([]);
    }
  }, [recentResults]);

  useUpDownSocket({
    socket,
    socketRef,
    currentPairRoom,
    variantRef,
    chartRef,
    setLivePrice,
    setClockNow,
    setTickerOffset,
    setPhase,
    setPhaseStartedAt,
    setPhaseEndsAt,
    setRoundDetails,
    setRoundStartPrice,
    setSettlement,
    setRoundBidsRefreshTick,
    setActiveOrders,
    setLatestOrder,
    setResultModalQueue,
    setWallet,
    getWallet,
    getLuckyRoundDetails,
    activeRoundStartRef,
    currentRoundIdRef,
    startingPriceRef,
    settledOrderIdsRef,
    resultShownIdsRef,
    settledToastKeysRef,
    orderRemovalTimersRef,
    backendIdToLocalIdRef,
    justSettledTradeRef,
    chartOrderHydrationRef,
    seenSpinCountRef,
  });

  const {
    handlePlaceOrder,
    handleOrderSettled,
    cancelOrderId,
    setCancelOrderId,
    cancelOrder,
    closeTradePreview,
    isCancelModalClosing,
    lastCancelOrderRef,
    lastClosePreviewRef,
    closeCancelModal,
    handleConfirmCancelOrder,
  } = useUpDownOrders({
    wallet,
    amount,
    amountInput,
    setAmountInput,
    symbolActiveOrdersCount,
    activePairLabel,
    chartRef,
    layoutRef,
    setActiveResultLayoutRef,
    tradingAPIRef,
    variant,
    upPayoutPercent,
    downPayoutPercent,
    selectedExpirySeconds,
    timerInput,
    panelId,
    luckyData,
    activeOrders,
    setActiveOrders,
    setLatestOrder,
    selectedPair,
    pairType,
    getWallet,
    livePrice,
    clockNow,
    tickerOffset,
    dynamicPayoutPercent,
    userRank,
    localResultOrderIdsRef,
    chartOrderHydrationRef,
    backendIdToLocalIdRef,
    ignoredBackendTradeIdsRef,
    orderRemovalTimersRef,
    settledOrderIdsRef,
  });

  const handleVariantChange = useCallback(
    (nextVariantId) => {
      setSelectedExpiryId(nextVariantId);

      if (nextVariantId === variant.id) {
        return;
      }

      const nextVariant =
        ROUND_VARIANTS.find((option) => option.id === nextVariantId) ??
        ROUND_VARIANTS[0];
      const clamped = clampStake(
        parseStake(amountInput),
        nextVariant.minimumStake,
        nextVariant.maximumStake,
      );
      setVariantId(nextVariant.id);
      setAmountInput(toAmountInput(clamped));
    },
    [amountInput, variant.id],
  );

  const handleCustomExpirySelect = useCallback(() => {
    setSelectedExpiryId(CUSTOM_EXPIRY_ID);
  }, []);

  const handleCustomExpiryBlur = useCallback(() => {
    setCustomExpirySeconds(
      String(
        clampCustomExpirySeconds(
          customExpirySeconds,
          DEFAULT_ORDER_DURATION_SECONDS,
        ),
      ),
    );
  }, [customExpirySeconds]);



  const CHART_ICONS = {
    candle: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M9 4H7V6H5V18H7V20H9V18H11V6H9V4ZM9 16H7V8H9V16ZM19 8H17V4H15V8H13V15H15V20H17V15H19V8ZM17 13H15V10H17V13Z" />
      </svg>
    ),
    area: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#clip0_92_326)">
          <path
            d="M0.689998 11.331L2.053 11.669L3.079 10.058L1.129 9.576C0.896271 9.51779 0.64995 9.55441 0.444222 9.67781C0.238495 9.8012 0.0902134 10.0013 0.0319984 10.234C-0.0262166 10.4667 0.0104033 10.7131 0.133802 10.9188C0.257201 11.1245 0.457271 11.2728 0.689998 11.331ZM18.481 11.592L14.018 15.608L8.771 11.547C8.67095 11.4699 8.55579 11.4147 8.433 11.385L7.735 11.211L6.708 12.822L7.808 13.095L13.505 17.503C13.6741 17.6332 13.8835 17.7001 14.0968 17.6923C14.3101 17.6844 14.5139 17.6023 14.673 17.46L19.701 12.933C19.7895 12.8537 19.8615 12.7576 19.9126 12.6503C19.9638 12.543 19.9932 12.4266 19.9992 12.3078C20.0051 12.1891 19.9875 12.0704 19.9473 11.9585C19.9071 11.8466 19.8451 11.7438 19.765 11.656C19.6031 11.4775 19.377 11.3705 19.1362 11.3585C18.8955 11.3465 18.6599 11.4305 18.481 11.592ZM8.684 7.18L13.571 10.309C13.7695 10.4359 14.0094 10.4812 14.2405 10.4353C14.4716 10.3895 14.676 10.2561 14.811 10.063L19.838 2.821C19.9063 2.72336 19.9546 2.61313 19.9799 2.49669C20.0053 2.38025 20.0073 2.25994 19.9858 2.14272C19.9644 2.02551 19.9198 1.91372 19.8548 1.81386C19.7897 1.714 19.7055 1.62805 19.607 1.561C19.4087 1.42398 19.1641 1.37123 18.9269 1.41436C18.6898 1.45748 18.4794 1.59294 18.342 1.791L13.814 8.312L8.898 5.165C8.69377 5.0351 8.44658 4.9909 8.21 5.042C8.093 5.06735 7.98224 5.11578 7.88419 5.18447C7.78615 5.25315 7.70279 5.3407 7.639 5.442L0.141998 17.209C0.0540864 17.3459 0.0047768 17.5039 -0.000720117 17.6665C-0.00621703 17.8291 0.0323028 17.9901 0.110769 18.1326C0.189236 18.2751 0.304735 18.3937 0.445063 18.476C0.585391 18.5583 0.745336 18.6011 0.907998 18.6C1.061 18.6002 1.21158 18.5618 1.34582 18.4884C1.48005 18.415 1.59361 18.3089 1.676 18.18L8.684 7.18Z"
            fill="white"
          />
        </g>
        <defs>
          <clipPath id="clip0_92_326">
            <rect width="18" height="18" fill="white" />
          </clipPath>
        </defs>
      </svg>
    ),
  };

  const chartIntervalButtons = CHART_INTERVALS.map((interval) => {
    const isLive = interval === "Tick";
    const label = isLive ? "Live" : interval;
    const isActive = timeframe === interval;
    
    return (
      <button
        key={interval}
        type="button"
        className={`updown-interval-pill ${isActive ? (isLive ? "is-live-active" : "is-active") : ""} ${isLive ? "is-live-btn" : ""}`}
        onClick={() => {
          const handledByChart = chartRef.current?.changeInterval?.(interval);
          if (!handledByChart) {
            onTimeframeChange(interval);
          }
        }}
      >
        {isLive && (
          <span className="updown-live-dot-wrap">
            <span className="updown-live-dot-ping" />
            <span className="updown-live-dot-core" />
          </span>
        )}
        <span>{label}</span>
      </button>
    );
  });

  const mobileChartIntervalButtons = chartIntervalButtons.filter(
    (btn) => btn.key === "Tick" || String(btn.key).includes("Tick")
  );

  const chartTypeButtons = timeframe === "Tick" ? null : ["candle", "area"].map((type) => (
    <div key={type}>
      <button
        type="button"
        className={`flex items-center justify-center  max-w-[200px] w-full gap-2 rounded-full h-6 md:h-8 w-full border px-3 py-1 text-xs font-semibold transition
        ${
          chartType === type
            ? "border-[#FC4420] bg-[#FC4420] text-white"
            : "border-white/10 bg-white/5 text-white/70 hover:border-white/20 hover:text-white"
        }`}
        onClick={() => onChartTypeChange(type)}
      >
        <span>{type === "candle" ? "Candle" : "Line"}</span>
        {CHART_ICONS[type]}
      </button>
    </div>
  ));

  // Zoom in/out buttons hidden per UI request
  const chartZoomButtons = null;

  const chartControls = (
    <div
      className={`updown-chart-controls mb-3 mt-2 flex  !felx-wrap items-center gap-2 ${layoutVariant ? " is-inline" : ""}`}
    >
      {chartIntervalButtons}
      {chartZoomButtons}
      {chartTypeButtons}
    </div>
  );

  const handleMobileExpiryChange = (event) => {
    const nextExpiryId = event.target.value;

    if (nextExpiryId === CUSTOM_EXPIRY_ID) {
      handleCustomExpirySelect();
      return;
    }

    handleVariantChange(nextExpiryId);
  };

  const mobileExpirySelect = (
    <div className="updown-mobile-field updown-mobile-expiry-field">
      <label htmlFor={`updown-mobile-expiry-${symbol}`}>Time</label>
      <select
        id={`updown-mobile-expiry-${symbol}`}
        value={selectedExpiryId}
        onChange={handleMobileExpiryChange}
        aria-label="Select trade expiry"
      >
        {dynamicExpiryOptions.slice(0, 4).map((option) => (
          <option key={option.id} value={option.id}>
            {option.seconds >= 60 ? "1 min" : `${option.seconds} sec`}
          </option>
        ))}
        <option value={CUSTOM_EXPIRY_ID}>Custom</option>
      </select>
    </div>
  );

  const timePickerOverlay = (
    <TimePickerOverlay
      isOpen={isTimePickerOpen}
      position={timePickerPosition}
      activeLayout={activeLayout}
      wallet={wallet}
      selectedExpirySeconds={selectedExpirySeconds}
      selectedExpiryId={selectedExpiryId}
      timerInput={timerInput}
      panelRef={timePickerPanelRef}
      scrollRef={timePickerScrollRef}
      selectedTimeOptionRef={selectedTimeOptionRef}
      onSelectOption={handleTimeOptionSelect}
      onClose={closeTimePicker}
    />
  );

  const amountPickerDropdown = (
    <AmountPickerDropdown
      isOpen={isAmountPickerOpen}
      position={amountPickerPosition}
      activeLayout={activeLayout}
      wallet={wallet}
      amount={amount}
      panelRef={amountPickerPanelRef}
      onSelectOption={handleAmountOptionSelect}
      onClose={closeAmountPicker}
    />
  );

  const ICON = {
    single: (
      <svg width="20" height="14" viewBox="0 0 20 14" fill="currentColor">
        <rect x="1" y="1" width="18" height="12" rx="2" />
      </svg>
    ),
    dual: (
      <svg width="20" height="14" viewBox="0 0 20 14" fill="currentColor">
        <rect x="1" y="1" width="8" height="12" rx="1.5" />
        <rect x="11" y="1" width="8" height="12" rx="1.5" />
      </svg>
    ),
    triple: (
      // 2+1: two panels on top, one full-width on bottom
      <svg width="20" height="14" viewBox="0 0 20 14" fill="currentColor">
        <rect x="1" y="1" width="8" height="5" rx="1.5" />
        <rect x="11" y="1" width="8" height="5" rx="1.5" />
        <rect x="1" y="8" width="18" height="5" rx="1.5" />
      </svg>
    ),
    quad: (
      <svg width="20" height="14" viewBox="0 0 20 14" fill="currentColor">
        <rect x="1" y="1" width="8" height="5" rx="1.5" />
        <rect x="11" y="1" width="8" height="5" rx="1.5" />
        <rect x="1" y="8" width="8" height="5" rx="1.5" />
        <rect x="11" y="8" width="8" height="5" rx="1.5" />
      </svg>
    ),
  };

  // useEffect(() => {
  //   setTimeout(() => {
  //     setShowJackpot(true);
  //   }, 100); // small delay for smooth entry
  // }, []);

  const isMulti = activeLayout !== "single";
  const isDesktop = typeof window !== "undefined" && window.innerWidth >= 1199;
  const useMultiDesktopLayout = isMulti && isDesktop;
  const isSingleLayout = activeLayout === "single";
  const resultPortalTarget =
    activeResultLayoutRef?.current ??
    (typeof document !== "undefined" ? document.body : null);
  const isResultPortalScoped = Boolean(activeResultLayoutRef?.current);
  const isCompactResultModal = layoutVariant && isResultPortalScoped;
  const shouldHideTradingChart = resultModal.isOpen || luckyModal.isOpen;

  return (
    <Card
      //  Add minimal dim/disable state while active orders are loading
      className={`updown-shell${layoutVariant ? " is-compact-shell" : ""}${isSingleLayout ? " is-single-layout" : ""} ${isLoadingActiveOrders ? "opacity-80 pointer-events-none grayscale-[20%]" : ""}`}
      style={
        useMultiDesktopLayout
          ? {
              width: "100%",
              height: "100%",
              flex: "1 1 0",
              alignSelf: "stretch",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              minHeight: 0,
            }
          : isSingleLayout
            ? {
                width: "100%",
                height: "100%",
                flex: "1 1 0",
                alignSelf: "stretch",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                minHeight: 0,
              }
            : undefined
      }
    >
      <div
        className="updown-shell__surface"
        style={
          useMultiDesktopLayout
            ? {
                width: "100%",
                height: "100%",
                flex: "1 1 0",
                alignSelf: "stretch",
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }
            : isSingleLayout
              ? {
                  width: "100%",
                  height: "100%",
                  flex: "1 1 0",
                  alignSelf: "stretch",
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }
              : undefined
        }
      >
        <div
          className={`updown-layout ${layoutVariant ? "is-compact" : ""}`}
          // className={`updown-layout ${shouldShowExpandedTradePanel ? 'has-trade-panel' : ''} ${layoutVariant ? 'is-compact' : ''}`}
          style={
            useMultiDesktopLayout
              ? {
                  display: "flex",
                  width: "100%",
                  height: "100%",
                  minWidth: 0,
                  minHeight: 0,
                  overflow: "hidden",
                }
              : layoutVariant
                ? {
                    display: "flex",
                    width: "100%",
                    height: "100%",
                    minWidth: 0,
                  }
                : isSingleLayout
                  ? {
                      display: "flex",
                      width: "100%",
                      height: "100%",
                      flex: "1 1 0",
                      alignSelf: "stretch",
                      minWidth: 0,
                      minHeight: 0,
                      overflow: "hidden",
                    }
                  : undefined
          }
        >
          <main
            className={`updown-main ${layoutVariant ? "updown-main--compact" : ""}${useMultiDesktopLayout ? ` updown-main--multi-desktop updown-main--${activeLayout}` : ""}`}
            style={
              useMultiDesktopLayout
                ? {
                    height: "100%",
                    display: "flex",
                    flexDirection: "row",
                    overflow: "hidden",
                    minHeight: 0,
                    flex: 1,
                    minWidth: 0,
                  }
                : layoutVariant
                  ? { flex: 1, minWidth: 0 }
                  : isSingleLayout
                    ? {
                        width: "100%",
                        height: "100%",
                        flex: "1 1 0",
                        alignSelf: "stretch",
                        minWidth: 0,
                        minHeight: 0,
                        display: "grid",
                        gridTemplateRows: "auto minmax(0, 1fr) auto",
                        overflow: "hidden",
                      }
                    : undefined
            }
          >
            <div className="chart-section">
              <UpDownToolbar
                pairTriggerRef={pairTriggerRef}
                isPairListOpen={isPairListOpen}
                setIsPairListOpen={setIsPairListOpen}
                selectedPair={selectedPair}
                marketSymbol={marketSymbol}
                activePairOption={activePairOption}
                symbol={symbol}
                activePairStats={activePairStats}
                wallet={wallet}
                dynamicPayoutPercent={dynamicPayoutPercent}
                mobileChartIntervalButtons={mobileChartIntervalButtons}
                chartControls={chartControls}
                chartTypeButtons={chartTypeButtons}
                playSound={playSound}
              />

              <section
                className={`updown-chart-block  ${layoutVariant ? " is-inline-controls" : ""}`}
                style={
                  useMultiDesktopLayout
                    ? {
                        width: "100%",
                        flex: 1,
                        minWidth: 0,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                      }
                    : isSingleLayout
                      ? {
                          width: "100%",
                          flex: 1,
                          minWidth: 0,
                          minHeight: 0,
                          display: "flex",
                          flexDirection: "column",
                          overflow: "hidden",
                        }
                      : undefined
                }
              >
                <div
                  className="updown-chart-frame"
                  style={
                    layoutVariant
                      ? {
                          width: "100%",
                          flex: 1,
                          minWidth: 0,
                          minHeight: 0,
                          overflow: "hidden",
                        }
                      : isSingleLayout
                        ? {
                            flex: 1,
                            minWidth: 0,
                            minHeight: 0,
                            overflow: "hidden",
                          }
                        : undefined
                  }
                >
                  <TradingChart
                    ref={chartRef}
                    symbol={symbol}
                    precision={precision}
                    tickSize={tickSize}
                      marketSymbol={marketSymbol}
                      startingPrice={startingPrice}
                      timeframe={timeframe}
                      chartType={timeframe === "Tick" ? "area" : chartType}
                      drawingEnabled={false}
                      drawingTool="trend"
                      drawingsResetKey={0}
                      disableInteractions={false}
                      hideTimeScale={false}
                      disableZoomToLatest={false}
                      feedMode={
                        pairType === "crypto" || pairType === "future"
                          ? "binance"
                          : "external"
                      }
                      fixedZoomPercent={null}
                      accurateLivePrice={timeframe !== "Tick"}
                      centeredVisibleBars={
                        timeframe === "Tick"
                          ? typeof window !== "undefined" && window.innerWidth < 640
                            ? 58
                            : 96
                          : null
                      }
                      enableSyntheticFallback={false}
                      tickVisibleHistoryPoints={2400}
                      forwardSpaceBars={chartForwardSpaceBars}
                      forceTickEveryMs={0}
                      smoothContinuousMotion
                      chartBackgroundColor="#7b7e814c"
                      chartFontSize={chartFontSize}
                      pinPriceScale={timeframe === "5s"}
                      cardClassName="updown-chart-card"
                      stageClassName="updown-chart-stage"
                      canvasClassName="updown-chart-canvas"
                      showLivePriceLine
                      showOrderTimeMarkers={true}
                      showLiveMarker
                      onTimeframeAutoChange={handleChartTimeframeAutoChange}
                      onOrderSettled={handleOrderSettled}
                      activeOrders={activeOrders}
                    />

                  {/* Chart status overlay hidden for now. */}

                  {resultModalQueue.map((result, index) => {
                    const raw = Number(result.profit);
                    const hasExplicitProfit = Number.isFinite(raw) && raw !== 0;
                    const isWon = result.status === "won";
                    const isDraw = result.status === "draw";
                    
                    let amountStr = "0.00";
                    let sign = "";
                    let cssClass = "is-draw";
                    
                    if (isWon) {
                      const winAmount = hasExplicitProfit ? Math.abs(raw) : Number(result.amount) || 0;
                      amountStr = formatUsd(winAmount);
                      sign = "+";
                      cssClass = "is-won";
                    } else if (isDraw) {
                      amountStr = "0.00";
                      sign = "";
                      cssClass = "is-draw";
                    } else {
                      const lossAmount = hasExplicitProfit ? Math.abs(raw) : Number(result.amount) || 0;
                      amountStr = formatUsd(lossAmount);
                      sign = "-";
                      cssClass = "is-lost";
                    }

                    return (
                      <div
                        key={result.id}
                        className={`rpg-floating-text ${cssClass}`}
                        style={{ top: `${36 + (index * 9)}%` }}
                      >
                        <div className="rpg-floating-badge">
                          <span className="rpg-floating-icon" aria-hidden="true">
                            {isWon ? (
                              <Sparkles size={18} strokeWidth={2.8} />
                            ) : isDraw ? (
                              <span className="rpg-draw-dash">—</span>
                            ) : (
                              <TrendingDown size={18} strokeWidth={2.8} />
                            )}
                          </span>
                          <span className="rpg-floating-amount">
                            <span className="rpg-floating-sign">{sign}</span>
                            <span className="rpg-floating-dollar">$</span>
                            <span className="rpg-floating-val">{amountStr}</span>
                          </span>
                          <div className="rpg-floating-sheen" aria-hidden="true" />
                        </div>
                      </div>
                    );
                  })}

                  <UpDownActiveTradesList
                    activeOrders={activeOrders}
                    orderStickyEndTimes={orderStickyEndTimes}
                    clockNow={clockNow}
                    chartRef={chartRef}
                    livePrice={livePrice}
                    wallet={wallet}
                    dynamicPayoutPercent={dynamicPayoutPercent}
                    userRank={userRank}
                    effectivePayoutMultiplier={effectivePayoutMultiplier}
                    setCancelOrderId={setCancelOrderId}
                  />
                </div>
              </section>
            </div>

            {/* {layoutVariant && ( */}
            <UpDownExecutionDock
              layoutVariant={layoutVariant}
              activeLayout={activeLayout}
              showJackpot={showJackpot}
              luckyData={luckyData}
              wallet={wallet}
              timePickerTriggerRef={timePickerTriggerRef}
              isTimePickerOpen={isTimePickerOpen}
              setIsTimePickerOpen={setIsTimePickerOpen}
              openTimePicker={openTimePicker}
              timerInput={timerInput}
              dynamicExpiryOptions={dynamicExpiryOptions}
              selectedExpiryId={selectedExpiryId}
              handleVariantChange={handleVariantChange}
              setTimerInput={setTimerInput}
              amountPickerTriggerRef={amountPickerTriggerRef}
              isAmountPickerOpen={isAmountPickerOpen}
              setIsAmountPickerOpen={setIsAmountPickerOpen}
              openAmountPicker={openAmountPicker}
              amountInput={amountInput}
              setAmountInput={setAmountInput}
              amount={amount}
              dynamicAmountOptions={dynamicAmountOptions}
              handlePlaceOrder={handlePlaceOrder}
              isTradeActionDisabled={isTradeActionDisabled}
              symbolActiveOrdersCount={symbolActiveOrdersCount}
              playSound={playSound}
            />
            {/* )} */}
          </main>

        

          {shouldShowLuckyModal && (
            <ResultModal
              open={luckyModal.isOpen}
              variant="multiplier"
              title="Combo Achieved!"
              multiplier={`${luckyModal.data?.multiplier ?? 0}X`}
              subtitle="This multiplier will take effect on your next prediction."
              subtitle2="Increase your prediction amount next round to maximize your potential reward."
              onClose={closeLuckyModal}
            />
          )}

         
        </div>
      </div>

     
      <EarlyCloseModal
        isOpen={(Boolean(cancelOrder) || isCancelModalClosing) && Boolean(closeTradePreview || lastClosePreviewRef.current)}
        isClosing={isCancelModalClosing}
        cancelOrder={cancelOrder}
        lastCancelOrderRef={lastCancelOrderRef}
        closeTradePreview={closeTradePreview}
        lastClosePreviewRef={lastClosePreviewRef}
        onClose={closeCancelModal}
        onConfirm={handleConfirmCancelOrder}
      />

      {timePickerOverlay}
      {amountPickerDropdown}

      <BinancePairsModal
        open={isPairListOpen}
        onOpenChange={setIsPairListOpen}
        pairs={pairOptions}
        selectedPair={selectedPair}
        pairType={pairType}
        onPairTypeChange={handlePairTypeChange}
        onSelectPair={onSelectPair}
        anchorRef={pairTriggerRef}
        hasMore={hasMorePairs}
        isLoadingMore={isLoadingMorePairs}
        onLoadMore={onLoadMorePairs}
      />
    
      <InFlightDuelOverlay
        duelState={settlementAura || "idle"}
        active={Boolean(settlementAura)}
      />
      {criticalWinState.isOpen && (
        <RpgCriticalWinBanner
          amount={criticalWinState.amount}
          streak={criticalWinState.streak}
          onClose={() => setCriticalWinState((prev) => ({ ...prev, isOpen: false }))}
        />
      )}
    </Card>
  );
}

export default UpDownTradingPanel;
